import { EMAIL_REGEX, sanitizeEmailInput, sanitizeInput } from '../../helpers/sanitizeInput.helper'
import { ORG_INVOICE_PROFILE_LIMITS } from '../constants/orgInvoiceProfile.constants'
import type { B2bAccountDto } from '../types/b2bAccount.types'

const L = ORG_INVOICE_PROFILE_LIMITS
const ISO_COUNTRY_REGEX = /^[A-Z]{2}$/

export interface OrgInvoiceProfileFormInput {
  billingContactName: string
  billingEmail: string
  billingAddressLine1: string
  billingAddressLine2: string
  billingCity: string
  billingStateOrRegion: string
  billingPostalCode: string
  billingCountry: string
  companyRegistrationNumber: string
  vatNumber: string
  purchaseOrderNumber: string
}

export type OrgInvoiceProfilePatch = Partial<{
  billingContactName: string | null
  billingEmail: string | null
  billingAddressLine1: string | null
  billingAddressLine2: string | null
  billingCity: string | null
  billingStateOrRegion: string | null
  billingPostalCode: string | null
  billingCountry: string | null
  companyRegistrationNumber: string | null
  vatNumber: string | null
  purchaseOrderNumber: string | null
}>

function cleanOptionalText(input: string, maxLength: number): string {
  const withoutNewlines = input.replace(/[\n\r]/g, '')
  const sanitized = sanitizeInput(withoutNewlines.replace(/<\/?script[^>]*>/gi, ''), true).trim()
  return sanitized.slice(0, maxLength)
}

function rawOrgInvoiceProfileFromAccount(account: B2bAccountDto): OrgInvoiceProfileFormInput {
  return {
    billingContactName: account.billingContactName ?? '',
    billingEmail: account.billingEmail ?? '',
    billingAddressLine1: account.billingAddressLine1 ?? '',
    billingAddressLine2: account.billingAddressLine2 ?? '',
    billingCity: account.billingCity ?? '',
    billingStateOrRegion: account.billingStateOrRegion ?? '',
    billingPostalCode: account.billingPostalCode ?? '',
    billingCountry: account.billingCountry ?? '',
    companyRegistrationNumber: account.companyRegistrationNumber ?? '',
    vatNumber: account.vatNumber ?? '',
    purchaseOrderNumber: account.purchaseOrderNumber ?? '',
  }
}

/** Map account DTO to form state; sanitize so legacy or tampered API values never render raw markup. */
export function orgInvoiceProfileFromAccount(account: B2bAccountDto): OrgInvoiceProfileFormInput {
  return sanitizeOrgInvoiceProfileInput(rawOrgInvoiceProfileFromAccount(account))
}

/** Sanitize a single invoice field on keystroke (uses full profile sanitizer for parity with save). */
export function sanitizeOrgInvoiceProfileField(
  field: keyof OrgInvoiceProfileFormInput,
  form: OrgInvoiceProfileFormInput,
  rawValue: string,
): string {
  const partial: OrgInvoiceProfileFormInput = {
    ...form,
    [field]: field === 'billingCountry' ? rawValue.toUpperCase() : rawValue,
  }
  return sanitizeOrgInvoiceProfileInput(partial)[field]
}

export function sanitizeOrgInvoiceProfileInput(
  input: OrgInvoiceProfileFormInput,
): OrgInvoiceProfileFormInput {
  return {
    billingContactName: cleanOptionalText(input.billingContactName, L.BILLING_CONTACT_NAME),
    billingEmail: sanitizeEmailInput(input.billingEmail).slice(0, L.BILLING_EMAIL),
    billingAddressLine1: cleanOptionalText(input.billingAddressLine1, L.BILLING_ADDRESS_LINE),
    billingAddressLine2: cleanOptionalText(input.billingAddressLine2, L.BILLING_ADDRESS_LINE),
    billingCity: cleanOptionalText(input.billingCity, L.BILLING_CITY),
    billingStateOrRegion: cleanOptionalText(input.billingStateOrRegion, L.BILLING_STATE_OR_REGION),
    billingPostalCode: cleanOptionalText(input.billingPostalCode, L.BILLING_POSTAL_CODE),
    billingCountry: cleanOptionalText(input.billingCountry, L.BILLING_COUNTRY).toUpperCase(),
    companyRegistrationNumber: cleanOptionalText(
      input.companyRegistrationNumber,
      L.COMPANY_REGISTRATION_NUMBER,
    ),
    vatNumber: cleanOptionalText(input.vatNumber, L.VAT_NUMBER),
    purchaseOrderNumber: cleanOptionalText(input.purchaseOrderNumber, L.PURCHASE_ORDER_NUMBER),
  }
}

export function validateOrgInvoiceProfilePatch(
  input: OrgInvoiceProfileFormInput,
): { ok: true; value: OrgInvoiceProfilePatch } | { ok: false; error: string } {
  const clean = sanitizeOrgInvoiceProfileInput(input)

  if (clean.billingEmail && !EMAIL_REGEX.test(clean.billingEmail)) {
    return { ok: false, error: 'Invoice delivery email must be valid' }
  }
  if (clean.billingCountry && !ISO_COUNTRY_REGEX.test(clean.billingCountry)) {
    return { ok: false, error: 'Country must be a 2-letter code (e.g. US, GB)' }
  }

  const toNullable = (value: string): string | null => (value.trim() ? value.trim() : null)

  return {
    ok: true,
    value: {
      billingContactName: toNullable(clean.billingContactName),
      billingEmail: toNullable(clean.billingEmail),
      billingAddressLine1: toNullable(clean.billingAddressLine1),
      billingAddressLine2: toNullable(clean.billingAddressLine2),
      billingCity: toNullable(clean.billingCity),
      billingStateOrRegion: toNullable(clean.billingStateOrRegion),
      billingPostalCode: toNullable(clean.billingPostalCode),
      billingCountry: toNullable(clean.billingCountry),
      companyRegistrationNumber: toNullable(clean.companyRegistrationNumber),
      vatNumber: toNullable(clean.vatNumber),
      purchaseOrderNumber: toNullable(clean.purchaseOrderNumber),
    },
  }
}
